// TL search bar.
class TLPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this._debounceTimer = null;
    this._activeFetch = null;
    this._lastQuery = '';
    this._abortCtrl = null;
    this._initialised = false;
    this._spotlightFetch = null;
    this._spotlightFetchPromise = null;
    this._spotlightContent = null;
    this._spotlightInserted = false;
    this._spotlightWarmupQueued = false;
    this._ignoreNextDocumentClick = false;
    this._blockNextOutsideClick = false;
    this._blockClickTimer = null;
    this._suppressFocusOpen = false;
    this._backdropPointerActive = false;
  }

  connectedCallback() {
    if (this._initialised) return;
    this._init();
  }

  _init = (event) => {
    if (this._initialised) return;
    this._initialised = true;

    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.form = this.querySelector('[data-search-form]');
    this.reset = this.querySelector('[data-search-reset]');
    this.panelClose = this.querySelector('[data-search-panel-close]');
    this.backdrop = this.querySelector('[data-search-backdrop]');
    this.spotlight = this.querySelector('[data-search-spotlight]');
    this.predictive = this.querySelector('[data-search-predictive]');
    this.liveRegion = this.querySelector('[data-search-live]');
    this.headerHost = this.closest('tl-header') || this;
    this.sectionId = this.dataset.sectionId || '';
    this.predictiveSectionId = this.dataset.predictiveSectionId || 'predictive-search';
    if (!this.input || !this.results || !this.form) return;

    this.searchUrl = this.dataset.searchUrl || '/search/suggest';
    this._t = {
      suggestions: this.dataset.textSuggestions || '',
      collections: this.dataset.textCollections || '',
      products: this.dataset.textProducts || '',
      viewAllQuery: this.dataset.textViewAllQuery || '',
      viewAllResultsQuery: this.dataset.textViewAllResultsQuery || '',
      noResults: this.dataset.textNoResults || '',
      unavailable: this.dataset.textUnavailable || '',
      resultsSummary: this.dataset.textResultsSummary || ''
    };
    this._abortCtrl = new AbortController();
    const opts = { signal: this._abortCtrl.signal };

    this.input.addEventListener('input', this._onInput.bind(this), opts);
    this.input.addEventListener('focus', this._onFocus.bind(this), opts);
    this.input.addEventListener('click', this._onFocus.bind(this), opts);
    this.input.addEventListener('keydown', this._onKeydown.bind(this), opts);
    this.form.addEventListener('pointerdown', this._onFormPointerDown.bind(this), opts);
    document.addEventListener('pointerdown', this._onOutsidePointerDown.bind(this), { ...opts, capture: true });
    document.addEventListener('click', this._onOutsideClickCapture.bind(this), { ...opts, capture: true });
    document.addEventListener('click', this._onDocumentClick.bind(this), opts);
    document.addEventListener('shopify:section:load', this._onSectionLoad.bind(this), opts);
    this.reset?.addEventListener('click', this._onReset.bind(this), opts);
    this.panelClose?.addEventListener('click', this._onPanelClose.bind(this), opts);
    this.backdrop?.addEventListener('pointerdown', this._onBackdropPointerDown.bind(this), opts);
    this.backdrop?.addEventListener('click', this._onBackdropClick.bind(this), opts);

    if (document.activeElement === this.input || event?.target === this.input) {
      window.requestAnimationFrame(() => this._onFocus());
    }

    this._scheduleSpotlightWarmup();
  };

  disconnectedCallback() {
    this._spotlightFetch?.abort();
    this._spotlightFetch = null;
    this._spotlightFetchPromise = null;
    this._spotlightContent = null;
    this._spotlightInserted = false;
    this._spotlightWarmupQueued = false;
    this._abortCtrl?.abort();
    this._abortCtrl = null;
    clearTimeout(this._debounceTimer);
    clearTimeout(this._blockClickTimer);
    this._ignoreNextDocumentClick = false;
    this._blockNextOutsideClick = false;
    this._backdropPointerActive = false;
    document.body.classList.remove('tl-search-open');
    this._initialised = false;
  }

  _onFocus() {
    if (this._suppressFocusOpen) return;
    const hasQuery = this.input.value.trim().length >= 2;
    if (hasQuery) {
      this._showPredictive();
      this._openResults();
      this._fetchResults(this.input.value.trim());
    } else {
      this._showSpotlight();
      this._openResults();
    }
  }

  // warmup only; WebKit-safe
  _onFormPointerDown(event) {
    if (event.target instanceof Element && event.target.closest('[data-search-form]')) {
      this._ignoreNextDocumentClick = true;
    }
    this._fetchSpotlightContent().catch(() => {});
  }

  _onInput() {
    const query = this.input.value.trim();
    clearTimeout(this._debounceTimer);
    if (query.length < 2) {
      this._showSpotlight();
      this._openResults();
      return;
    }
    this._debounceTimer = setTimeout(() => this._fetchResults(query), 100);
  }

  _onKeydown(event) {
    if (event.key === 'Escape') {
      this._fullClose();
      return;
    }
    const items = this._allResultsItems;
    if (!items.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;
    const currentIndex = this._currentIndex;
    const total = items.length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this._currentIndex = currentIndex < total - 1 ? currentIndex + 1 : 0;
        break;
      case 'ArrowUp':
        event.preventDefault();
        this._currentIndex = currentIndex > 0 ? currentIndex - 1 : total - 1;
        break;
      case 'Enter':
        if (currentIndex >= 0) {
          const item = this._currentItem;
          const link = item?.tagName === 'A' ? item : item?.querySelector('a');
          if (link?.href) {
            event.preventDefault();
            window.location.href = link.href;
          }
        }
        break;
    }
  }

  // Keyboard targets.
  get _allResultsItems() {
    if (!this.predictive || this.predictive.hidden) return [];
    return Array.from(
      this.predictive.querySelectorAll(
        '.search-results__query, .search-results__product-navigation, ' +
        '.search-results__view-all, .search-results__view-all-inline'
      )
    );
  }

  get _currentIndex() {
    return this._allResultsItems.findIndex(
      (item) => item.getAttribute('aria-selected') === 'true'
    );
  }

  set _currentIndex(index) {
    const items = this._allResultsItems;
    if (!items.length) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }
    let activeId = '';
    items.forEach((item, i) => {
      if (!item.id) item.id = `tl-search-option-${i}`;
      item.setAttribute('role', 'option');
      if (i === index) {
        item.setAttribute('aria-selected', 'true');
        activeId = item.id;
      } else {
        item.removeAttribute('aria-selected');
      }
    });
    if (activeId) {
      this.input.setAttribute('aria-activedescendant', activeId);
      items[index]?.scrollIntoView({
        behavior: this._prefersReducedMotion() ? 'instant' : 'smooth',
        block: 'nearest'
      });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
    this.input.focus();
  }

  get _currentItem() { return this._allResultsItems[this._currentIndex]; }

  _prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Result indexing.
  _indexResults() {
    this._allResultsItems.forEach((item, i) => {
      if (!item.id) item.id = `tl-search-option-${i}`;
      item.setAttribute('role', 'option');
    });
  }

  // Live announcements.
  _announce(message) {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = '';
    requestAnimationFrame(() => {
      if (this.liveRegion) this.liveRegion.textContent = message;
    });
  }

  // editor re-init hook
  _onSectionLoad(event) {
    const target = event?.target;
    if (!(target instanceof HTMLElement) || !target.contains(this)) return;
    this.disconnectedCallback();
    this._init();
  }

  _onDocumentClick(event) {
    if (this._ignoreNextDocumentClick) {
      this._ignoreNextDocumentClick = false;
      return;
    }

    if (!this.headerHost.contains(event.target)) this._closeResults();
  }

  _isOpen() {
    return this.classList.contains('is-open');
  }

  _isInsideSearch(target) {
    return target instanceof Node && this.headerHost.contains(target);
  }

  _onOutsidePointerDown(event) {
    if (!this._isOpen() || this._isInsideSearch(event.target)) return;
    // only a primary press yields a paired click; touch-scroll/right-click do not
    if (event.button === 0 && event.isPrimary !== false) {
      this._blockNextOutsideClick = true;
      // self-clear if no click follows (touch scroll-start, cancelled press)
      clearTimeout(this._blockClickTimer);
      this._blockClickTimer = window.setTimeout(() => {
        this._blockNextOutsideClick = false;
      }, 700);
    }
    event.preventDefault();
    event.stopPropagation();
    this._closeResults();
    this.input.blur();
  }

  _onOutsideClickCapture(event) {
    if (this._blockNextOutsideClick) {
      this._blockNextOutsideClick = false;
      clearTimeout(this._blockClickTimer);
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!this._isOpen() || this._isInsideSearch(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
    this._closeResults();
    this.input.blur();
  }

  _fullClose() {
    this.input.value = '';
    this._lastQuery = '';
    if (this.predictive) this.predictive.replaceChildren();
    if (this.spotlight) this.spotlight.hidden = false;
    if (this.predictive) this.predictive.hidden = true;
    this._closeResults();
  }

  _onReset() {
    this._fullClose();
  }

  _onPanelClose() {
    this._fullClose();
    // restore focus to the trigger input without reopening the panel
    this._suppressFocusOpen = true;
    this.input.focus();
    this._suppressFocusOpen = false;
  }

  _onBackdropPointerDown(event) {
    this._backdropPointerActive = event.target === this.backdrop;
  }

  _onBackdropClick(event) {
    const isKeyboardClick = event.detail === 0;
    if (!this._backdropPointerActive && !isKeyboardClick) return;

    this._backdropPointerActive = false;
    this._closeResults();
    this.input.blur();
  }

  _toggleReset(show) {
    if (this.reset) this.reset.hidden = !show;
  }

  async _fetchResults(query) {
    if (query === this._lastQuery) {
      this._openResults();
      return;
    }
    this._lastQuery = query;
    this._activeFetch?.abort();
    this._activeFetch = new AbortController();

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,query,collection',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor'
    });

    const url = `${this.searchUrl}.json?${params.toString()}`;

    try {
      const controller = this._activeFetch;
      const productMarkup = this._fetchProductMarkup(query, controller.signal).catch(() => null);
      const res = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (controller.signal.aborted || query !== this._lastQuery) return;

      this._render(data, query);
      this._openResults();

      productMarkup.then((productList) => {
        if (!productList || controller.signal.aborted || query !== this._lastQuery || !this._isOpen()) return;
        if (!this.predictive || this.predictive.hidden) return;
        this._render(data, query, productList, { announce: false, resetScroll: false }, true);
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        this._renderMessage(this._t.unavailable);
        this._openResults();
        this._announce(this._t.unavailable);
      }
    }
  }

  _render(data, query, productList = null, options = {}, appendOnly = false) {
    const shouldAnnounce = options.announce !== false;
    const shouldResetScroll = options.resetScroll !== false;
    const resources = data?.resources?.results || {};
    const queries = resources.queries || [];
    const products = resources.products || [];
    const collections = resources.collections || [];
    const totalCount = queries.length + products.length + collections.length;

    this._showPredictive();

    if (appendOnly && productList) {
      const existingProducts = this.predictive.querySelector('.search-results__products .predictive-search-results__wrapper-products');
      if (existingProducts) {
        existingProducts.replaceWith(productList);
        this._indexResults();
        return;
      }
    }

    if (!totalCount) {
      const empty = this._createElement('p', 'search-results__empty');
      this._appendTemplateWithQuery(empty, this._t.noResults, query, true);
      const children = [empty];
      const fallback = this._cloneSpotlightFallback();
      if (fallback) children.push(fallback);
      this.predictive.replaceChildren(...children);
      if (shouldAnnounce) this._announce(this._textWithQuery(this._t.noResults, query));
      this._indexResults();
      if (shouldResetScroll) this._scrollResultsToTop();
      return;
    }

    const aside = this._renderAside(queries, collections, query) || this._cloneSpotlightAsideFallback();
    const productsSection = this._renderProducts(products, query, productList);
    const grid = this._createElement(
      'div',
      `search-results__grid ${aside ? 'search-results__grid--with-aside' : 'search-results__grid--products-only'}`
    );
    if (aside) grid.append(aside);
    if (productsSection) grid.append(productsSection);

    const children = [grid];
    if (!productsSection) {
      const bottomViewAll = this._createElement('a', 'search-results__view-all', {
        href: this._searchRoute(query)
      });
      bottomViewAll.textContent = this._textWithQuery(this._t.viewAllResultsQuery, query);
      children.push(bottomViewAll);
    }

    this.predictive.replaceChildren(...children);
    if (shouldAnnounce) {
      this._announce(
        this._t.resultsSummary
          .replace('__COUNT__', String(totalCount))
          .replace('__QUERY__', query)
      );
    }
    this._indexResults();
    if (shouldResetScroll) this._scrollResultsToTop();
  }

  _showSpotlight() {
    if (this.spotlight) this.spotlight.hidden = false;
    if (this.predictive) this.predictive.hidden = true;
    this._hydrateSpotlight();
    this._scrollResultsToTop();
  }

  _showPredictive() {
    if (this.spotlight) {
      this.spotlight.hidden = true;
      this.spotlight.replaceChildren();
      this.spotlight.removeAttribute('aria-busy');
      this._spotlightInserted = false;
    }
    if (this.predictive) this.predictive.hidden = false;
  }

  async _hydrateSpotlight({ keepHidden = false } = {}) {
    if (!this.spotlight || this._spotlightInserted) return;

    if (this._hasRenderedSpotlightContent()) {
      this._spotlightInserted = true;
      return;
    }

    if (this._spotlightContent) {
      this.spotlight.replaceChildren(document.importNode(this._spotlightContent, true));
      this._spotlightInserted = true;
      return;
    }

    this.spotlight.setAttribute('aria-busy', 'true');

    try {
      const content = await this._fetchSpotlightContent();
      if (!content) {
        if (!keepHidden) this.spotlight.hidden = true;
        return;
      }

      if (!this._isOpen() || this.spotlight.hidden || this._spotlightInserted) return;

      this.spotlight.replaceChildren(document.importNode(content, true));
      this._spotlightInserted = true;
    } catch (err) {
      if (err.name !== 'AbortError') this.spotlight.hidden = true;
    } finally {
      this.spotlight.removeAttribute('aria-busy');
    }
  }

  _hasRenderedSpotlightContent() {
    return Boolean(
      this.spotlight?.querySelector('[data-search-spotlight-content], .spotlight__empty')
    );
  }

  _spotlightFallbackSource() {
    if (this._hasRenderedSpotlightContent()) return this.spotlight;
    if (this._spotlightContent) return this._spotlightContent;
    return null;
  }

  _scheduleSpotlightWarmup() {
    if (!this.spotlight || this._spotlightWarmupQueued || this._spotlightContent) return;
    this._spotlightWarmupQueued = true;

    const runWarmup = () => {
      if (this._abortCtrl?.signal.aborted || this._spotlightContent) return;
      this._fetchSpotlightContent().catch(() => {});
    };

    // hover-intent pre-warm
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.form.addEventListener('pointerenter', runWarmup, { signal: this._abortCtrl.signal, once: true });
    }
  }

  async _fetchSpotlightContent() {
    if (this._spotlightContent) return this._spotlightContent;
    if (this._spotlightFetchPromise) return this._spotlightFetchPromise;

    const sourceUrl = this.spotlight?.dataset.searchSpotlightUrl;
    if (!sourceUrl) return null;

    this._spotlightFetch?.abort();
    const controller = new AbortController();
    this._spotlightFetch = controller;
    this._spotlightFetchPromise = (async () => {
      const res = await fetch(sourceUrl, {
        signal: controller.signal,
        headers: { Accept: 'text/html' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const content =
        doc.querySelector('[data-search-spotlight-content]') ||
        doc.querySelector('.spotlight__empty');

      this._spotlightContent = content || null;
      // warm card images
      content?.querySelectorAll('img').forEach((img) => {
        const warm = new Image();
        const srcset = img.getAttribute('srcset');
        if (srcset) {
          warm.srcset = srcset;
          warm.sizes = img.getAttribute('sizes') || '';
        }
        warm.src = img.getAttribute('src') || '';
      });
      return this._spotlightContent;
    })();

    try {
      return await this._spotlightFetchPromise;
    } finally {
      this._spotlightFetchPromise = null;
      if (this._spotlightFetch === controller) this._spotlightFetch = null;
    }
  }

  _renderAside(queries, collections, query) {
    if (!queries.length && !collections.length) return null;
    const aside = this._createElement('aside', 'search-results__aside');
    if (queries.length) {
      aside.append(this._renderLinkBlock(this._t.suggestions, queries, query, (item) => item.text, (item) => item.url, true, 'suggestions'));
    }
    if (collections.length) {
      aside.append(this._renderLinkBlock(this._t.collections, collections, query, (item) => item.title, (item) => item.url, false, 'collections'));
    }
    return aside;
  }

  _renderProducts(products, query, productList = null) {
    if (!products.length) return null;
    const section = this._createElement('section', 'search-results__products');
    const head = this._createElement('div', 'search-results__products-head');
    const title = this._createElement('h3', 'search-results__title');
    title.textContent = this._t.products;
    head.append(title);

    const list = productList || this._renderJsonProductList(products);
    if (!list) return null;

    const viewAll = this._createElement('a', 'search-results__view-all-inline', {
      href: this._searchRoute(query)
    });
    viewAll.textContent = this._textWithQuery(this._t.viewAllQuery, query);

    section.append(head, list, viewAll);
    return section;
  }

  _renderJsonProductList(products) {
    const list = this._createElement(
      'ul',
      'predictive-search-results__wrapper-products search-results__json-products',
      { role: 'list' }
    );

    products.slice(0, 4).forEach((product) => {
      const item = this._createElement('li', 'predictive-search-results__card--product search-results__json-product');
      const link = this._createElement('a', 'search-results__json-product-link search-results__product-navigation', {
        href: product.url || this._searchRoute(product.title || '')
      });
      const image = product.featured_image?.url || product.image;

      if (image) {
        const img = this._createElement('img', 'search-results__json-product-image', {
          src: this._imageUrl(image, 360),
          srcset: `${this._imageUrl(image, 120)} 120w, ${this._imageUrl(image, 180)} 180w, ${this._imageUrl(image, 240)} 240w, ${this._imageUrl(image, 360)} 360w`,
          sizes: '60px',
          alt: product.featured_image?.alt || product.title || '',
          loading: 'lazy',
          decoding: 'async',
          width: product.featured_image?.width || 360,
          height: product.featured_image?.height || 360
        });
        link.append(img);
      }

      const content = this._createElement('span', 'search-results__json-product-content');
      if (product.vendor) {
        const vendor = this._createElement('span', 'search-results__json-product-vendor');
        vendor.textContent = product.vendor;
        content.append(vendor);
      }
      const name = this._createElement('span', 'search-results__json-product-title');
      name.textContent = product.title || '';
      content.append(name);
      link.append(content);
      item.append(link);
      list.append(item);
    });

    return list.children.length ? list : null;
  }

  async _fetchProductMarkup(query, signal) {
    if (!this.predictiveSectionId) return null;

    const url = new URL(this.searchUrl, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', this.predictiveSectionId);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');

    const res = await fetch(url, { signal, headers: { Accept: 'text/html' } });
    if (!res.ok) return null;

    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const list = doc.querySelector('#predictive-search-products .predictive-search-results__wrapper-products');
    if (!list) return null;

    list.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    list.querySelectorAll('[aria-labelledby]').forEach((element) => element.removeAttribute('aria-labelledby'));
    return list;
  }

  _renderMessage(message) {
    if (!this.predictive) return;
    this._showPredictive();
    const empty = this._createElement('p', 'search-results__empty');
    empty.textContent = message;
    this.predictive.replaceChildren(empty);
    this._scrollResultsToTop();
  }

  _renderLinkBlock(titleText, items, query, textGetter, urlGetter, shouldHighlight = false, blockType = '') {
    const section = this._createElement(
      'section',
      `search-results__block${blockType ? ` search-results__block--${blockType}` : ''}`
    );
    const title = this._createElement('h3', 'search-results__title');
    title.textContent = titleText;
    const list = this._createElement('ul', 'search-results__list', { role: 'list' });
    items.forEach((item) => {
      const row = document.createElement('li');
      const link = this._createElement('a', 'search-results__query', { href: urlGetter(item) || this._searchRoute(textGetter(item) || '') });
      if (shouldHighlight) {
        this._appendHighlightedText(link, textGetter(item), query);
      } else {
        link.textContent = textGetter(item) || '';
      }
      row.append(link);
      list.append(row);
    });
    section.append(title, list);
    return section;
  }

  _appendHighlightedText(target, text, query) {
    if (!text) return;
    try {
      const re = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig');
      let lastIndex = 0;
      String(text).replace(re, (match, offset) => {
        if (offset > lastIndex) target.append(document.createTextNode(String(text).slice(lastIndex, offset)));
        const mark = document.createElement('mark');
        mark.textContent = match;
        target.append(mark);
        lastIndex = offset + match.length;
        return match;
      });
      if (lastIndex < String(text).length) target.append(document.createTextNode(String(text).slice(lastIndex)));
    } catch {
      target.textContent = text;
    }
  }

  _appendTemplateWithQuery(target, template, query, strongQuery = false) {
    const parts = String(template || '').split('__QUERY__');
    parts.forEach((part, index) => {
      if (part) target.append(document.createTextNode(part));
      if (index < parts.length - 1) {
        const node = strongQuery ? document.createElement('strong') : document.createTextNode(query);
        node.textContent = query;
        target.append(node);
      }
    });
  }

  _textWithQuery(template, query) {
    return String(template || '').replace('__QUERY__', query);
  }

  _cloneSpotlightFallback() {
    const source = this._spotlightFallbackSource();
    if (!source) return null;
    const fallback = this._createElement('div', 'search-results__empty-fallback');
    const clone = document.importNode(source, true);
    if ('hidden' in clone) clone.hidden = false;
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    clone.querySelectorAll('[aria-labelledby]').forEach((el) => el.removeAttribute('aria-labelledby'));
    fallback.append(clone);
    return fallback;
  }

  _cloneSpotlightAsideFallback() {
    const source = this._spotlightFallbackSource()?.querySelector('.spotlight__aside');
    if (!source) return null;

    const aside = this._createElement('aside', 'search-results__aside search-results__aside--fallback');
    const clone = source.cloneNode(true);
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    clone.querySelectorAll('[aria-labelledby]').forEach((el) => el.removeAttribute('aria-labelledby'));

    while (clone.firstChild) aside.append(clone.firstChild);
    return aside.children.length ? aside : null;
  }

  _createElement(tag, className, attributes = {}) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    Object.entries(attributes).forEach(([name, value]) => {
      if (value != null && value !== false) element.setAttribute(name, String(value));
    });
    return element;
  }

  _scrollResultsToTop() {
    if (this.results) this.results.scrollTop = 0;
  }

  _searchRoute(query) {
    return `/search?q=${encodeURIComponent(query)}`;
  }

  _imageUrl(url, width) {
    try {
      const imageUrl = new URL(url, window.location.origin);
      imageUrl.searchParams.set('width', String(width));
      return imageUrl.toString();
    } catch {
      return url;
    }
  }

  _openResults() {
    this._toggleReset(true);
    this.input.setAttribute('aria-expanded', 'true');
    if (this.backdrop) this.backdrop.hidden = false;
    this.classList.add('is-open');
    document.body.classList.add('tl-search-open');
  }

  _closeResults() {
    this._toggleReset(false);
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.classList.remove('is-open');
    document.body.classList.remove('tl-search-open');
    this._clearPanelContent();
    const hideBackdrop = () => {
      if (!this.classList.contains('is-open') && this.backdrop) this.backdrop.hidden = true;
    };
    if (this._prefersReducedMotion() || !this.backdrop) {
      hideBackdrop();
    } else {
      const backdropFallback = window.setTimeout(hideBackdrop, 400);
      this.backdrop.addEventListener('transitionend', () => {
        clearTimeout(backdropFallback);
        hideBackdrop();
      }, { once: true });
    }
    this._allResultsItems.forEach((item) => item.removeAttribute('aria-selected'));
  }

  _clearPanelContent() {
    clearTimeout(this._debounceTimer);
    this._activeFetch?.abort();
    this._activeFetch = null;
    this._spotlightFetch?.abort();
    this._spotlightFetch = null;
    this._spotlightFetchPromise = null;
    this._spotlightInserted = false;
    this._lastQuery = '';

    if (this.spotlight) {
      this.spotlight.replaceChildren();
      this.spotlight.hidden = false;
      this.spotlight.removeAttribute('aria-busy');
    }
    if (this.predictive) {
      this.predictive.replaceChildren();
      this.predictive.hidden = true;
    }
  }
}

if (!customElements.get('tl-predictive-search')) {
  customElements.define('tl-predictive-search', TLPredictiveSearch);
}
